import { Button, Typography } from '@s4l/ds'
import { Link } from '@s4l/react-facade'
import { cn } from '@s4l/utility-css'
import { showLoginModal } from '@s4l/utils/browser/eventBridge'
import { Devices, useBrowser } from '@s4l/utils/hooks/useBrowser'
import React from 'react'
import { useBanner } from '../hooks'
import type { BannersJSON } from '../types'
import { BANNER_TYPES } from '../types'
import styles from './index.module.css'

interface BillboardProps {
  bannersJSON?: BannersJSON
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

function Billboard({ bannersJSON, onClick }: Readonly<BillboardProps>) {
  const { device } = useBrowser()

  const { bannerInformation } = useBanner({
    bannersJSON,
    bannerType: BANNER_TYPES.BILLBOARD,
  })

  if (!bannerInformation) return null

  const title = bannerInformation.title?.[0] ?? ''
  const description = bannerInformation.description ?? ''

  const { bgColor, titleColor, descriptionColor } = bannerInformation

  const getImageSet = (srcSet: string[] = []) => {
    const [mobile, tablet, desktop] = srcSet
    return [mobile || '', tablet || mobile || '', desktop || tablet || mobile || '']
  }

  const [mobileImage, tabletImage, desktopImage] = getImageSet(bannerInformation.image?.srcSet)

  const imageAlt = bannerInformation.image?.alt || undefined

  const CTAtext = bannerInformation.ctaText ?? null
  const showCTA = Boolean(CTAtext && bannerInformation.url)

  const isMobile = device === Devices.Mobile

  const titleTypographyProps = isMobile
    ? ({ variant: 'body', size: 'l' } as const)
    : ({ variant: 'title', size: 'xs' } as const)

  const handleLoginClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    showLoginModal('billboard-banner')
  }

  const bannerDiv = (
    <div
      className={cn(styles.bannerBase, { [styles.withCTA]: showCTA })}
      style={
        {
          '--banner-bg-color': bgColor,
          '--banner-background-image-mobile': `url(${mobileImage})`,
          '--banner-background-image-tablet': `url(${tabletImage})`,
          '--banner-background-image-desktop': `url(${desktopImage})`,
        } as React.CSSProperties
      }
      aria-label={imageAlt}
      data-testid="billboard-banner"
    >
      <div className={styles.bannerContent}>
        <Typography
          {...titleTypographyProps}
          bold
          as="h6"
          color={titleColor || '--color-neutral-900'}
          clamp={2}
        >
          {title}
        </Typography>
        <Typography
          variant="body"
          size="l"
          as="p"
          color={descriptionColor || '--color-neutral-900'}
          clamp={4}
        >
          {description}
        </Typography>
      </div>
      {showCTA && (
        <Button variant="filled-rounded" size="small">
          {CTAtext}
        </Button>
      )}
    </div>
  )

  if (bannerInformation.url) {
    const shouldShowLoginModal = bannerInformation.url === '/login'

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick(e)
      }

      if (!e.defaultPrevented) {
        if (shouldShowLoginModal) {
          handleLoginClick(e)
        }
      }
    }

    return (
      <Link
        href={shouldShowLoginModal ? '#' : bannerInformation.url}
        onClick={handleClick}
        className={styles.bannerLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        {bannerDiv}
      </Link>
    )
  }

  return bannerDiv
}

Billboard.displayName = 'Billboard'
export default Billboard
